import NotificationItem from "./NotificationItem";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import CloseIcon from "@mui/icons-material/Close";
import { useNotifications } from "../context/NotificationContext";
import { markAsRead } from "../firebase/services/notificationServices";
import { useRouter } from "@/lib/navigation";
import styles from "./NotificationsModal.module.css";
import { useCallback, useRef, useEffect } from "react";
import NotificationSkeleton from "./NotificationSkeleton";

export default function NotificationsModal({ isOpen, onClose }) {
  const { notifications, isLoading, hasMore, loadMore } = useNotifications();
  const router = useRouter();
  const observer = useRef();
  const lastNotificationRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, loadMore]
  );

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(styles.modalOpen);
    } else {
      document.body.classList.remove(styles.modalOpen);
    }

    return () => {
      document.body.classList.remove(styles.modalOpen);
    };
  }, [isOpen]);

  const handleNotificationClick = async (notification) => {
    if (
      notification.type === "FOLLOW_REQUEST" &&
      !notification.metadata?.handled
    ) {
      return;
    }

    if (!notification.read) {
      await markAsRead(notification.id);
    }

    onClose();

    switch (notification.type) {
      case "LIKE_REVIEW":
        router.push(
          `/books/${notification.metadata.bookId}#review-${notification.sourceId}`
        );
        break;
      case "NEW_FOLLOWER":
      case "FOLLOW_REQUEST":
        router.push(`/users/${notification.metadata.actorName}`);
        break;
      case "BOOK_CLUB_UPDATE":
        router.push(`/book-club/${notification.metadata.bookClubId}`);
        break;
      case "READING_GOAL_ACHIEVED":
        router.push(`/users/${notification.metadata.username}/goals`);
        break;
      case "COMMENT_ON_REVIEW":
        router.push(
          `/books/${notification.metadata.bookId}#review-${notification.sourceId}`
        );
        break;
      case "LIST_FOLLOWER":
        router.push(`/lists/${notification.metadata.listId}`);
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Notifications</h2>
          <button onClick={onClose} className={styles.closeButton}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.notificationList}>
          {isLoading && notifications.length === 0 ? (
            Array(3)
              .fill(0)
              .map((_, i) => <NotificationSkeleton key={i} />)
          ) : !notifications || notifications.length === 0 ? (
            <div className={styles.emptyState}>
              <NotificationsOffIcon className={styles.emptyStateIcon} />
              <div>No notifications found</div>
            </div>
          ) : (
            <>
              {notifications.map((notification, index) => (
                <div
                  ref={
                    index === notifications.length - 1
                      ? lastNotificationRef
                      : null
                  }
                  key={notification.id}
                >
                  <NotificationItem
                    notification={notification}
                    onClick={() => handleNotificationClick(notification)}
                  />
                </div>
              ))}
              {isLoading && (
                <div className={styles.loadingMore}>
                  <NotificationSkeleton />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
